import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Modal } from '@/components/ui';
import styles from './accountDialogs.module.css';

export interface OneTimeSecretModalProps {
    /** `null` keeps it closed. Present means a secret is on screen right now. */
    secret: OneTimeSecret | null;
    onClose: () => void;
}

export interface OneTimeSecret {
    username: string;
    /** Absent in `invite` mode — Keycloak mailed a set-password link instead. */
    tempPassword?: string;
    /** Shown only on create; a reset has no new sub to report. */
    sub?: string;
    /** Create vs reset — only the wording differs. */
    kind: 'created' | 'reset';
}

/**
 * EAA-1820: frame 66 — "Mật khẩu tạm — hiện đúng một lần".
 *
 * THIS IS A MODAL BECAUSE THE RULE SAYS SO, and the previous shape broke it.
 * The handoff is explicit that the one-time password is "không bao giờ là nội
 * dung tĩnh của trang" — never static page content. It used to render as a
 * `SectionCard` in the page body: a card sits in the normal flow, survives
 * scrolling past it, and is one screenshot or one shoulder away from leaking a
 * live credential that the server does not store and cannot reissue.
 *
 * A modal is the only shape that matches "hiện đúng một lần": it is dismissed
 * deliberately, it cannot be scrolled past, and once closed the caller drops the
 * value and there is no path that renders it again — `onClose` clears the state
 * that held it, and nothing refetches it, because the server never kept it.
 *
 * The value is therefore NEVER put in route state, storage, or a toast; it lives
 * only in the caller's component state for as long as this modal is open.
 *
 * EAA-1804: buttons swapped to the kit `Button` instead of hand-written
 * `<button className="portal-btn ...">`; the saved-ack checkbox swapped to
 * the kit `Checkbox` instead of a hand-wrapped `<input type="checkbox">`.
 */
export function OneTimeSecretModal({ secret, onClose }: OneTimeSecretModalProps) {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    // EAA-1820 (frame 71): the footer checkbox — operator must acknowledge
    // before "Xong" enables, matching the handoff's saved-password gate.
    const [savedAck, setSavedAck] = useState(false);

    async function copy(value: string) {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard denied (insecure origin, permission) — the value is on
            // screen and selectable, so this is not worth an error state.
        }
    }

    function handleClose() {
        setCopied(false);
        setSavedAck(false);
        onClose();
    }

    const isReset = secret?.kind === 'reset';
    const title = isReset
        ? t('console.accounts.secretModal.resetTitle')
        : t('console.accounts.secretModal.createdTitle');
    const hasPassword = Boolean(secret?.tempPassword);
    const canClose = !hasPassword || savedAck;

    return (
        // EAA-1804: root debugging handle. "acc-secret-modal" rather than "acc-secret"
        // — that name is already taken by the inner content div below.
        <Modal
            className="acc-secret-modal"
            open={secret !== null}
            onCancel={handleClose}
            title={title}
            width={520}
            footer={
                <div className={styles.secretFooter}>
                    {hasPassword && (
                        <Checkbox
                            // EAA-1846: the e2e spec used to select this checkbox by its
                            // class name; the class is hashed now, so the test hooks onto a
                            // stable `data-testid` instead of a styling handle.
                            data-testid="secret-saved-ack"
                            className={styles.secretSavedAck}
                            checked={savedAck}
                            onChange={(e) => setSavedAck(e.target.checked)}
                        >
                            {t('console.accounts.secretModal.savedAckLabel')}
                        </Checkbox>
                    )}
                    <Button type="primary" onClick={handleClose} disabled={!canClose}>
                        {t('console.accounts.secretModal.savedButton')}
                    </Button>
                </div>
            }
        >
            <div className={styles.secret}>
                <p className={styles.secretLead}>
                    {isReset
                        ? t('console.accounts.secretModal.resetLead')
                        : t('console.accounts.secretModal.createdLead')}
                </p>

                <dl className={styles.secretRows}>
                    <div className={styles.secretRow}>
                        <dt className={styles.secretTerm}>{t('console.accounts.secretModal.usernameLabel')}</dt>
                        <dd className={styles.secretDef}>
                            <code className={styles.secretCode}>{secret?.username}</code>
                        </dd>
                    </div>
                    {secret?.sub && (
                        <div className={styles.secretRow}>
                            <dt className={styles.secretTerm}>{t('console.accounts.secretModal.subLabel')}</dt>
                            <dd className={styles.secretDef}>
                                <code className={styles.secretCode}>{secret.sub}</code>
                            </dd>
                        </div>
                    )}
                    {hasPassword && (
                        <div className={`${styles.secretRow} ${styles.secretRowSecret}`}>
                            <dt className={styles.secretTerm}>{t('console.accounts.secretModal.tempPasswordLabel')}</dt>
                            <dd className={styles.secretDef}>
                                <code className={`${styles.secretCode} ${styles.secretValue}`}>
                                    {secret?.tempPassword}
                                </code>
                                <Button onClick={() => copy(secret?.tempPassword as string)}>
                                    {copied
                                        ? t('console.accounts.secretModal.copiedButton')
                                        : t('console.accounts.secretModal.copyButton')}
                                </Button>
                            </dd>
                        </div>
                    )}
                </dl>

                {hasPassword ? (
                    /* Required copy, verbatim from the frame — it is the whole
                       reason the modal cannot be reopened. */
                    <p className={styles.secretWarn} role="alert">
                        {t('console.accounts.secretModal.closeWarnsLoss')}
                    </p>
                ) : (
                    <p className={styles.secretWarn}>
                        {t('console.accounts.secretModal.inviteSentNote')}
                    </p>
                )}
            </div>
        </Modal>
    );
}

export default OneTimeSecretModal;
